import { Box, Typography, useMediaQuery } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  useAthleteEducationLevelsQuery,
  useAthletesDistributionBySportQuery,
} from '../../../store/apiSlice/analytics/analyticsApi';
import JobPostFilterSingleSelect from '../../../pages/JobPostsPage/components/JobPostsSidebar/JobPostsFilters/JobPostFilterSingleSelect';
import createArrayFromEnum from '../../../utils/createArrayFromEnum';
import { DemographicsInsightEnum } from '../../../enums/demographics-insight-enum';
import { TDemographicsInsight } from '../../../interfaces/jobpost.interface';
import AthleteDistribution from './LineRangeAnalytics/AthleteDistribution';
import { useThemeContext } from '../../../theme/ThemeContextProvider';
import { EducationLevelEnum } from '../../../enums/education-type-enum';
import SkeletonList from '../../MUIComponents/SkeletonList';

const getEducationLevelName = (level: EducationLevelEnum) => {
  switch (level) {
    case EducationLevelEnum.ELEMENTARY:
      return 'Elementary';
    case EducationLevelEnum.SECONDARY:
      return 'Secondary';
    case EducationLevelEnum.BACHELOR:
      return "Bachelor's degree";
    case EducationLevelEnum.MASTER:
      return "Master's degree";
    case EducationLevelEnum.DOCTORAL:
      return 'Doctorate degree';
    default:
      return 'Unknown';
  }
};
const DemographicInsight = () => {
  const { theme } = useThemeContext();
  const mobileView = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [statisticType, setStatisticType] = useState<string>(
    DemographicsInsightEnum.ATHLETE_DISTRIBUTION_BY_SPORT
  );

  const demographicsInsightArray: TDemographicsInsight[] = createArrayFromEnum(
    DemographicsInsightEnum,
    'demographicsInsight'
  );

  const {
    data: athletesDistributionBySportData,
    isLoading: isAthletesDistributionBySportLoading,
    refetch: refetchAthletesDistributionBySport,
  } = useAthletesDistributionBySportQuery(undefined, {
    skip:
      statisticType !== DemographicsInsightEnum.ATHLETE_DISTRIBUTION_BY_SPORT,
  });

  const {
    data: athleteEducationLevelsData,
    isLoading: isAthleteEducationLevelsLoading,
    refetch: refetchAthleteEducationLevels,
  } = useAthleteEducationLevelsQuery(undefined, {
    skip:
      statisticType !== DemographicsInsightEnum.EDUCATION_LEVELS_OF_APPLICANTS,
  });

  useEffect(() => {
    if (
      statisticType === DemographicsInsightEnum.ATHLETE_DISTRIBUTION_BY_SPORT
    ) {
      refetchAthletesDistributionBySport();
    }

    if (
      statisticType === DemographicsInsightEnum.EDUCATION_LEVELS_OF_APPLICANTS
    ) {
      refetchAthleteEducationLevels();
    }
  }, [
    statisticType,
    refetchAthletesDistributionBySport,
    refetchAthleteEducationLevels,
  ]);

  const filteredData = useMemo(() => {
    if (
      !athletesDistributionBySportData ||
      !athletesDistributionBySportData.data.sports
    )
      return [];

    const sportsData = Object.keys(
      athletesDistributionBySportData.data.sports
    ).map(sport => ({
      value: sport,
      candidates: athletesDistributionBySportData.data.sports[sport].count,
      percentage: athletesDistributionBySportData.data.sports[sport].percentage,
    }));

    return sportsData.sort((a, b) => b.candidates - a.candidates);
  }, [athletesDistributionBySportData]);

  const educationLevelsData = useMemo(() => {
    if (
      !athleteEducationLevelsData ||
      !athleteEducationLevelsData.data.educations
    )
      return [];

    const educationLevels = Object.keys(
      athleteEducationLevelsData.data.educations
    ).map(level => ({
      value: getEducationLevelName(level as EducationLevelEnum),
      candidates: athleteEducationLevelsData.data.educations[level].count,
      percentage: athleteEducationLevelsData.data.educations[level].percentage,
    }));

    return educationLevels.sort((a, b) => b.candidates - a.candidates);
  }, [athleteEducationLevelsData]);

  const renderComponent = useMemo(() => {
    switch (statisticType) {
      case DemographicsInsightEnum.ATHLETE_DISTRIBUTION_BY_SPORT:
        return isAthletesDistributionBySportLoading ? (
          <Box
            width={'100%'}
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            height={'6.25rem'}
            padding={isSmallScreen ? '10px 16px 16px 16px' : '22px 36px'}
          >
            <SkeletonList
              sx={{
                width: '100%',
                minHeight: '4rem',
              }}
              count={1}
            />
          </Box>
        ) : (
          <AthleteDistribution
            data={filteredData}
            totalCandidates={
              athletesDistributionBySportData?.data?.candidates ?? 0
            }
          />
        );
      // TODO: Will be implemented in the future
      // case DemographicsInsightEnum.SOURCE_OF_HIRE:
      //   return (
      //     <Typography variant={'caption'} padding="22px 36px">
      //       {`"Source of hire" will go here.`}
      //     </Typography>
      //   );
      case DemographicsInsightEnum.EDUCATION_LEVELS_OF_APPLICANTS:
        return isAthleteEducationLevelsLoading ? (
          <Box
            width={'100%'}
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            height={'6.25rem'}
            padding={isSmallScreen ? '10px 16px 16px 16px' : '22px 36px'}
          >
            <SkeletonList
              sx={{
                width: '100%',
                minHeight: '4rem',
              }}
              count={1}
            />
          </Box>
        ) : (
          <AthleteDistribution
            data={educationLevelsData}
            totalCandidates={athleteEducationLevelsData?.data?.candidates ?? 0}
          />
        );
      default:
        return null;
    }
  }, [
    statisticType,
    isAthletesDistributionBySportLoading,
    isSmallScreen,
    filteredData,
    athletesDistributionBySportData?.data?.candidates,
    isAthleteEducationLevelsLoading,
    educationLevelsData,
    athleteEducationLevelsData?.data?.candidates,
  ]);

  return (
    <Box width="100%" height="fit-content" bgcolor="#fff">
      <Box
        borderBottom="1px solid #E8EBEF"
        padding={isSmallScreen ? '20px 16px' : '20.5px 36px'}
      >
        <Typography
          sx={{
            fontSize: '20px',
          }}
          variant={'h3'}
        >
          Demographic Insight
        </Typography>
      </Box>

      <Box
        width={mobileView ? '100%' : '50%'}
        maxWidth={mobileView ? '100%' : '400px'}
        padding={isSmallScreen ? '22px 16px 12px 16px' : '22px 36px'}
      >
        <JobPostFilterSingleSelect
          label={'demographicsInsight'}
          placeholder={'Choose type of statistics:'}
          selectItems={demographicsInsightArray}
          onChange={setStatisticType}
          value={statisticType}
        />
      </Box>
      <Box
        id="pdf-bottom-demographics"
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        width={'100%'}
      >
        {renderComponent}
      </Box>
    </Box>
  );
};

export default DemographicInsight;
